import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, Globe, Users, ChevronDown } from "lucide-react";
import Footer from "@/components/Footer";
import FacebookSection from "@/components/FacebookSection";
import { trackPageView } from "@/lib/analytics";

export default function Home() {
  useEffect(() => { trackPageView("home"); }, []);
  const scrollToContent = () => {
    document.getElementById("facebook-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div
          className="hero-bg"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1694852239278-f83d94af390e?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        />
        <div className="hero-overlay" />

        <div className="hero-content max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="max-w-4xl">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/60 border border-slate-700 mb-8 animate-fade-in"
              data-testid="hero-badge"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-medium text-slate-300 tracking-wide uppercase">
                Protect Our Legacy
              </span>
            </div>

            {/* Main Heading */}
            <h1
              className="font-serif text-5xl md:text-7xl font-bold text-slate-50 leading-tight mb-6 animate-fade-in-up"
              data-testid="hero-title"
            >
              Ireland's Neutrality
              <br />
              <span className="text-gradient-tricolor">Must Be Protected</span>
            </h1>

            {/* Subheading */}
            <p
              className="text-lg md:text-xl text-slate-300 leading-relaxed mb-10 max-w-2xl animate-fade-in-up delay-200"
              data-testid="hero-subtitle"
            >
              For generations, Ireland has stood as a beacon of peace, serving
              with distinction in UN peacekeeping missions across the globe.
              Our neutrality is not weakness—it is our strength.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
              <Link to="/take-action">
                 {/* <Button
                  data-testid="hero-cta-primary"
                  className="btn-glow bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-6 rounded-full text-lg font-bold transition-all hover:-translate-y-1 shadow-[0_0_30px_-5px_rgba(22,163,74,0.5)] hover:shadow-[0_0_40px_-5px_rgba(22,163,74,0.7)]"
                >
                  Email Your TD Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>*/}
              </Link>
              <Link to="/about">
                <Button
                  data-testid="hero-cta-secondary"
                  variant="outline"
                  className="bg-transparent border-2 border-slate-600 hover:border-slate-400 text-slate-200 hover:text-white px-8 py-6 rounded-full text-lg font-medium transition-all"
                >
                  Learn Why It Matters
                </Button>
              </Link>
            </div>
          </div>

          {/* Scroll indicator */}
          <button
            onClick={scrollToContent}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-400 hover:text-white transition-colors animate-bounce"
            aria-label="Scroll to content"
          >
            <ChevronDown className="w-8 h-8" />
          </button>
        </div>
      </section>

      {/* Facebook Section */}
      <FacebookSection />

      {/* TDs Who Voted to Remove the Triple Lock */}
      <section className="py-24 bg-slate-950" data-testid="vote-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-sm font-semibold tracking-widest uppercase text-orange-500">
              Dáil Vote — 21 October 2026
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-semibold text-slate-100 mt-4 mb-6">
              79 TDs Voted to Remove the Triple Lock
            </h2>
            <p className="text-lg text-slate-400">
              On 21 October 2026, the Defence (Amendment) Bill 2026 passed Second Stage by 79 votes to 71. These are the TDs who voted <strong className="text-orange-400">Tá (Yes)</strong> to dismantle the Triple Lock.
            </p>
          </div>

          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 md:p-10">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {[
                "William Aird", "Catherine Ardagh", "Grace Boland", "Tom Brabazon",
                "Brian Brennan", "Shay Brennan", "Colm Brophy", "James Browne",
                "Colm Burke", "Mary Butler", "Paula Butterly", "Malcolm Byrne",
                "Thomas Byrne", "Michael Cahill", "Catherine Callaghan", "Dara Calleary",
                "Seán Canney", "Micheál Carrigy", "Jennifer Carroll MacNeill", "Jack Chambers",
                "Peter 'Chap' Cleere", "John Clendennen", "Niall Collins", "John Connolly",
                "Joe Cooney", "Cathal Crowe", "John Cummins", "Emer Currie",
                "Martin Daly", "Aisling Dempsey", "Cormac Devlin", "Alan Dillon",
                "Albert Dolan", "Frankie Feighan", "Sean Fleming", "Norma Foley",
                "Pat the Cope Gallagher", "James Geoghegan", "Noel Grealish", "Marian Harkin",
                "Martin Heydon", "Emer Higgins", "Keira Keogh", "Seán Kyne",
                "John Lahart", "Michael Lowry", "Micheál Martin", "David Maxwell",
                "Paul McAuliffe", "Noel McCarthy", "Charlie McConalogue", "Tony McCormack",
                "Helen McEntee", "Séamus McGrath", "Erin McGreehan", "Kevin Boxer Moran",
                "Aindrias Moynihan", "Michael Moynihan", "Shane Moynihan",
                "Jennifer Murnane O'Connor", "Michael Murphy", "Joe Neville",
                "Darragh O'Brien", "Jim O'Callaghan", "Maeve O'Connell", "James O'Connor",
                "Kieran O'Donnell", "Ryan O'Meara", "John Paul O'Shea",
                "Christopher O'Sullivan", "Pádraig O'Sullivan", "Naoise Ó Cearúil",
                "Seán Ó Fearghaíl", "Peter Roche", "Eamon Scanlon", "Brendan Smith",
                "Edward Timmins", "Gillian Toole", "Barry Ward"
              ].map((name) => (
                <div
                  key={name}
                  className="px-3 py-2 bg-slate-800/60 border border-slate-700/50 rounded-lg text-sm text-slate-300 text-center"
                >
                  {name}
                </div>
              ))}
            </div>

            <div className="mt-8 text-center space-y-4">
              <p className="text-lg font-semibold text-slate-100">
                Email your constituency TDs from the above list using the template provided.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/take-action">
                  <Button
                    data-testid="vote-cta-btn"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-5 rounded-full text-base font-bold transition-all hover:-translate-y-1 shadow-[0_0_30px_-5px_rgba(22,163,74,0.5)]"
                  >
                    Email Your TD Now
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a
                  href="https://www.oireachtas.ie/en/debates/vote/dail/34/2026-10-21/234/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="vote-source-link"
                  className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors"
                >
                  View full vote record on Oireachtas.ie
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Peacekeeper Feature Section */}
      <section className="py-24 bg-slate-950" data-testid="peacekeeper-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative animate-slide-in-left">
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-900/30 to-orange-900/30 rounded-3xl blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl border border-slate-800">
                <img
                  src="https://images.unsplash.com/photo-1748529289784-d2fb55fedce5?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Military Peacekeeping Operation"
                  className="w-full h-[500px] object-cover hover:scale-105 transition-all duration-700"
                  data-testid="peacekeeper-image"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-slate-950 to-transparent">
                  <p className="text-sm text-slate-400 font-medium">
                    Peacekeeping in Action — Protecting Global Stability
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
              <span className="text-sm font-semibold tracking-widest uppercase text-orange-500">
                Our Proud Heritage
              </span>
              <h2
                className="font-serif text-3xl md:text-5xl font-semibold text-slate-100 leading-tight"
                data-testid="heritage-title"
              >
                Guardians of Peace,
                <br />
                <span className="text-emerald-400">Not Warriors of War</span>
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                Since 1958, Irish soldiers have served in over 60 UN peacekeeping
                missions. Our troops are respected worldwide for their impartiality,
                professionalism, and commitment to peace. This legacy was built on
                the foundation of Irish neutrality.
              </p>
              <div className="pt-4 flex gap-8">
                <div>
                  <p className="text-4xl font-bold text-emerald-400">60+</p>
                  <p className="text-sm text-slate-400 mt-1">UN Missions</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-emerald-400">87</p>
                  <p className="text-sm text-slate-400 mt-1">Lives Given</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-emerald-400">65+</p>
                  <p className="text-sm text-slate-400 mt-1">Years of Service</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Neutrality Section */}
      <section
        id="why-neutrality"
        className="py-24 md:py-32 bg-slate-900"
        data-testid="why-neutrality-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-semibold tracking-widest uppercase text-orange-500">
              Why It Matters
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-semibold text-slate-100 mt-4 mb-6">
              Neutrality Is Ireland's Strength
            </h2>
            <p className="text-lg text-slate-400">
              Our neutral stance gives us a unique voice in international affairs
              and protects our sovereignty.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div
              className="card-dark p-8 card-lift"
              data-testid="feature-card-1"
            >
              <div className="w-14 h-14 rounded-xl bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="font-serif text-xl font-medium text-slate-100 mb-3">
                Sovereign Independence
              </h3>
              <p className="text-slate-400 leading-relaxed">
                Neutrality ensures Ireland makes foreign policy decisions in our
                national interest, free from military alliance obligations.
              </p>
            </div>

            {/* Card 2 */}
            <div
              className="card-dark p-8 card-lift"
              data-testid="feature-card-2"
            >
              <div className="w-14 h-14 rounded-xl bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center mb-6">
                <Globe className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="font-serif text-xl font-medium text-slate-100 mb-3">
                Trusted Mediator
              </h3>
              <p className="text-slate-400 leading-relaxed">
                As a neutral nation, Ireland can serve as an honest broker in
                international disputes and humanitarian efforts.
              </p>
            </div>

            {/* Card 3 */}
            <div
              className="card-dark p-8 card-lift"
              data-testid="feature-card-3"
            >
              <div className="w-14 h-14 rounded-xl bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="font-serif text-xl font-medium text-slate-100 mb-3">
                Public Support
              </h3>
              <p className="text-slate-400 leading-relaxed">
                Polls consistently show the Irish people strongly support
                maintaining neutrality—this is the will of the nation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Banner */}
      <section className="py-20 bg-slate-950 relative overflow-hidden" data-testid="cta-banner">
        {/* Gradient accent */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/10 via-transparent to-orange-900/10" />
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-100 mb-6">
            Your Voice Matters
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
            Contact your local representatives today and tell them you support
            Ireland's neutrality. We've made it easy with a ready-to-send
            email template.
          </p>
          <Link to="/take-action">
            <Button
              data-testid="cta-banner-btn"
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-10 py-6 rounded-full text-lg font-bold transition-all hover:-translate-y-1 shadow-[0_0_30px_-5px_rgba(22,163,74,0.5)] animate-pulse-glow"
            >
              Email Your Representatives
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
